/*
 * FileClaimDialog · ClaimEvidenceGallery
 *
 * Buyer-side dialog to file a dropship claim against a specific order.
 * Always lets the buyer attach 1-3 photos of evidence; photos are
 * REQUIRED when `kind === "damaged"` because there's no carrier-side
 * trail for damage (unlike late-delivery / no-movement which Verqo Shipping
 * already documents).
 *
 * The companion `<ClaimEvidenceGallery>` renders the photos on either
 * the buyer's confirmation drawer or the seller's claim review drawer
 * — same component, two viewers.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Image as ImageIcon, Plus, X, Camera, AlertTriangle, Loader2 } from "lucide-react";
import api from "../../lib/api";

const MAX_PHOTOS = 3;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;  // 5 MB per photo, kept small at the UI edge

const KIND_OPTIONS = [
  { value: "damaged",       label: "Damaged",         hint: "Item arrived broken or visibly damaged",
    photoMode: "required" },
  { value: "wrong_item",    label: "Wrong item",      hint: "Different SKU or variant than ordered",
    photoMode: "optional" },
  { value: "late_delivery", label: "Late delivery",   hint: "Delivered past the SLA — carrier-trackable",
    photoMode: "optional" },
  { value: "no_movement",   label: "No movement",     hint: "Tracking hasn't updated in 96+ hours",
    photoMode: "optional" },
  { value: "other",         label: "Other",           hint: "Custom claim per your bilateral terms",
    photoMode: "optional" },
];

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result || ""));
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function FileClaimDialog({ open, onClose, order, onSuccess }) {
  const [kind, setKind] = useState("damaged");
  const [description, setDescription] = useState("");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [photos, setPhotos] = useState([]);  // [{data_url, caption}]
  const [submitting, setSubmitting] = useState(false);

  const kindMeta = KIND_OPTIONS.find(k => k.value === kind);
  const photoRequired = kindMeta?.photoMode === "required";

  // Reset state on open/close so each dialog instance starts clean.
  useEffect(() => {
    if (!open) return;
    setKind("damaged");
    setDescription("");
    setRequestedAmount("");
    setPhotos([]);
    setSubmitting(false);
  }, [open]);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";  // allow re-picking the same file
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) return toast.error(`Up to ${MAX_PHOTOS} photos`);
    const accepted = [];
    for (const f of files.slice(0, remaining)) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name}: not an image`);
        continue;
      }
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name}: over 5 MB — please compress`);
        continue;
      }
      const data_url = await fileToDataUrl(f);
      accepted.push({ data_url, caption: "" });
    }
    if (accepted.length) setPhotos([...photos, ...accepted]);
  };

  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!description.trim()) return toast.error("Describe the issue");
    if (photoRequired && photos.length < 1) {
      return toast.error("Damaged claims require at least 1 photo of the damage");
    }
    if (photos.length > MAX_PHOTOS) {
      return toast.error(`Up to ${MAX_PHOTOS} photos`);
    }
    setSubmitting(true);
    try {
      const body = {
        order_id: order.id,
        kind,
        description: description.trim(),
        requested_amount: requestedAmount ? Number(requestedAmount) : undefined,
        evidence_photos: photos.length ? photos : undefined,
      };
      const { data } = await api.post("/dropship/claims", body);
      toast.success(`Claim filed · ${data.id?.slice(0, 8)}…`);
      onSuccess?.(data);
      onClose?.();
    } catch (e) {
      const msg = e.response?.data?.detail;
      toast.error(Array.isArray(msg) ? msg[0]?.msg || "Failed" : (msg || "Failed to file claim"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;
  const orderValue = (order.wholesale_cost || 0) * (order.qty || 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="file-claim-dialog">
        <DialogHeader>
          <DialogTitle>File a claim · {order.code}</DialogTitle>
          <DialogDescription>
            We'll auto-grade the deduction off your bilateral agreement with {order.seller_name || "the supplier"}.
            Order value <strong>${orderValue.toFixed(2)}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Kind selector — pill buttons */}
          <div>
            <Label>Claim type</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mt-1.5">
              {KIND_OPTIONS.map(o => (
                <button key={o.value} type="button" onClick={() => setKind(o.value)}
                  data-testid={`claim-kind-${o.value}`}
                  className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                    kind === o.value
                      ? "bg-foreground text-background border-foreground"
                      : "bg-white border-border hover:bg-secondary"
                  }`}>
                  <div className="text-[12.5px] font-medium">{o.label}</div>
                  <div className={`text-[10.5px] mt-0.5 ${kind === o.value ? "text-background/70" : "text-foreground/55"}`}>{o.hint}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <Label>What happened?</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)}
              data-testid="claim-description"
              placeholder="e.g. The cleansing oil bottle arrived shattered — leaked all over the box"
              rows={3} className="mt-1.5"/>
          </div>

          {/* Requested amount (optional) */}
          <div>
            <Label>Requested credit · optional</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55 text-[12px]">$</span>
              <Input value={requestedAmount} onChange={e => setRequestedAmount(e.target.value)}
                type="number" step="0.01" min="0" max={orderValue}
                placeholder={`Leave blank to auto-grade · max $${orderValue.toFixed(2)}`}
                className="pl-7"
                data-testid="claim-requested-amount"/>
            </div>
            <div className="text-[11px] text-foreground/55 mt-1">
              We default to the bilateral agreement's responsibility rule. Override if you and your supplier negotiated something different.
            </div>
          </div>

          {/* Photo evidence — REQUIRED for damaged */}
          <div className={`rounded-xl border p-3 ${
            photoRequired ? "border-rose-200 bg-rose-50/30" : "border-border bg-secondary/20"
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Camera className={`w-4 h-4 ${photoRequired ? "text-rose-700" : "text-foreground/55"}`}/>
              <div className="text-[12.5px] font-medium">
                Evidence photos {photoRequired
                  ? <span className="text-rose-700"> · required (1-{MAX_PHOTOS})</span>
                  : <span className="text-foreground/55"> · optional (up to {MAX_PHOTOS})</span>}
              </div>
            </div>
            {photoRequired && (
              <div className="text-[11.5px] text-rose-800 inline-flex items-center gap-1 mb-2">
                <AlertTriangle className="w-3 h-3"/>
                Damaged claims require photo proof — there's no carrier-side trail like there is for late-delivery.
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-lg border border-border bg-white overflow-hidden group">
                  <img src={p.data_url} alt={`Evidence ${i+1}`} className="w-full h-full object-cover"/>
                  <button onClick={() => removePhoto(i)} type="button"
                    data-testid={`remove-claim-photo-${i}`}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white grid place-items-center hover:bg-black">
                    <X className="w-3 h-3"/>
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label className={`aspect-square rounded-lg border-2 border-dashed grid place-items-center cursor-pointer transition-colors ${
                  photoRequired && photos.length === 0
                    ? "border-rose-300 hover:bg-rose-50"
                    : "border-border hover:bg-secondary"
                }`} data-testid="add-claim-photo">
                  <input type="file" accept="image/*" multiple onChange={onPickPhotos} className="hidden"/>
                  <div className="flex flex-col items-center gap-1 text-foreground/55">
                    <Plus className="w-5 h-5"/>
                    <span className="text-[10px] mono uppercase tracking-[0.14em]">Add photo</span>
                  </div>
                </label>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !description.trim() || (photoRequired && photos.length < 1)}
            data-testid="submit-claim-btn"
            className="bg-foreground text-background hover:bg-foreground/90">
            {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>}
            File claim
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Renders the evidence photos attached to a claim. Used on BOTH the
 * buyer's claim detail drawer (their own photos, read-only) and the
 * seller's claim review drawer (verifying what the buyer submitted).
 *
 * In list view, the API returns lightweight metadata (count + has_data
 * flags). When the seller/buyer expands a claim drawer, the parent fetches
 * the single-claim endpoint to get full base64 bodies. This component
 * lazy-fetches if it sees `has_data` flags but no `data_url`.
 */
export function ClaimEvidenceGallery({ claim, onLoad }) {
  const [full, setFull] = useState(null);  // full claim with data_url-loaded photos
  const photos = full?.evidence_photos || claim?.evidence_photos || [];
  const needsFetch = useMemo(() =>
    photos.length > 0 && photos.some(p => p.has_data && !p.data_url),
    [photos]);

  useEffect(() => {
    if (!needsFetch || !claim?.id) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/dropship/claims/${claim.id}`);
        if (alive) {
          setFull(data);
          onLoad?.(data);
        }
      } catch { /* silent — gallery just stays in skeleton */ }
    })();
    return () => { alive = false; };
  }, [claim?.id, needsFetch, onLoad]);

  if (!photos.length) return null;
  return (
    <div className="rounded-xl border border-border bg-white p-3">
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/65 inline-flex items-center gap-1 mb-2">
        <ImageIcon className="w-3 h-3"/>Evidence photos ({photos.length})
      </div>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p, i) => (
          <div key={i} className="aspect-square rounded-lg border border-border overflow-hidden bg-secondary/20">
            {p.data_url ? (
              <a href={p.data_url} target="_blank" rel="noreferrer"
                 data-testid={`claim-evidence-photo-${i}`} title="Open full-size">
                <img src={p.data_url} alt={`Evidence ${i+1}`} className="w-full h-full object-cover hover:opacity-90"/>
              </a>
            ) : (
              <div className="w-full h-full grid place-items-center text-foreground/40 text-[10px]">
                <Loader2 className="w-4 h-4 animate-spin"/>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
